import * as React from "react";
import { graphql } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import { Media } from "../components/Media";
import Seo from "../components/Seo";
import AussieOwned from "../components/seals/AustralianOwned";
import Vegan from "../components/seals/VeganFriendly";
import GlutenFree from "../components/seals/GlutenFree";
import SugarFree from "../components/seals/SugarFree";
import StickyWrapper from "../components/StickyWrapper";
import CustomerQuote from "../components/CustomerQuote";
import Plant from "../components/chillOut/Plant";
import TopCurve from "../components/TopCurve";
import ogImage from "../images/chillOut/chill-out-yoga.jpg";
import "../styles/chillOut.css";

const ChillOut = ({ data }) => {
  const product = getImage(data.product);
  const products = getImage(data.products);
  const yoga = getImage(data.yoga);
  const busy = getImage(data.busy);
  const zen = getImage(data.zen);
  const lemonBalm = getImage(data.lemonBalm);
  const kavaKava = getImage(data.kavaKava);
  const passionflower = getImage(data.passionflower);
  const magnolia = getImage(data.magnolia);
  const ziziphus = getImage(data.ziziphus);

  return (
    <>
      <Seo
        title="Chill Out™: Stress &amp; Sleep Support"
        description="A combination of natural herbs traditionally used in Western medicine to assist with stress, sleep and mild anxiety. Made in Australia."
        image={`https://www.ithrivenutrition.com.au${ogImage}`}
        alt="Chill Out bottle on a calm sunset backdrop."
        handle="chill-out"
      />
      <div className="bg-gray-darker">
        <div className="max-w-screen-lg m-auto pt-2 md:pt-6 lg:pt-10">
          <StickyWrapper
            title="Chill Out"
            subtitle="Gentle Herbal Tonic For Mild Anxiety, Stress And Sleep"
            shopLink="chill-out"
          />
          <div className="flex flex-wrap-reverse md:flex-nowrap">
            <div className="text-white mt-0 md:mt-10 w-full px-6 md:p-0 md:w-5/12">
              <p>
                <strong>Chill Out™</strong> is a comprehensive sleep, calming
                and relaxing formula combining premium herbal extracts
                traditionally used in Western medicine to assist with:
              </p>
              <ul className="list-inside mt-2">
                <li className="list-circle white">
                  Stress, tension and nervous unrest
                </li>
                <li className="list-circle white">
                  Reducing symptoms of mild anxiety
                </li>
                <li className="list-circle white">Relieving restless sleep</li>
              </ul>

              <div className="flex justify-center my-10">
                <AussieOwned fill="#FFBA09" />
                <Vegan fill="#FFBA09" />
                <SugarFree fill="#FFBA09" />
                <GlutenFree fill="#FFBA09" />
              </div>
            </div>
            <div className="w-full text-right -mb-8 md:mb-0 md:w-7/12 relative">
              <Media lessThan="md">
                <GatsbyImage
                  image={product}
                  alt="Chill Out bottle"
                  className="bottle-hero"
                />
              </Media>
              <Media greaterThanOrEqual="md">
                <GatsbyImage
                  image={products}
                  alt="Chill Out bottles"
                  className="bottles-hero"
                  style={{ position: "absolute" }}
                />
              </Media>
            </div>
          </div>
        </div>
      </div>
      <div className="yellow-gradient"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 relative overflow-hidden">
        <div>
          <div
            className="relative z-1 md:absolute md:w-1/2"
            style={{
              background:
                "linear-gradient(180deg, rgba(91,88,134,1) 0%, rgba(139,103,134,1) 68%, rgba(169,120,136,0) 100%)",
            }}
          >
            <h2 className="text-40 leading-tight text-center text-white p-8 pb-20 -mb-20">
              Take a <br />
              <span style={{ color: "#F9BA99" }}>Chill Pill</span>
            </h2>
          </div>
          <div id="yogaHero" className="h-96 md:h-full">
            <BgImage
              image={yoga}
              style={{ height: "100%", backgroundPosition: "55% 80%" }}
              className="xl:mt-10"
            />
          </div>
        </div>
        <div>
          <Media lessThan="md">
            <div
              className="h-12 -mt-12 relative"
              style={{
                background:
                  "linear-gradient(180deg, rgba(22,20,28,0) 0%, rgba(27,30,39,1) 100%)",
              }}
            ></div>
          </Media>
          <div className="p-8 pb-12 md:p-20 lg:py-28 xl:py-36 2xl:py-40 bg-gray-dark md:bg-white">
            <p className="text-30 text-white md:text-gray-mid mb-4">
              <strong>Do you sometimes...</strong>
            </p>
            <ul className="text-white md:text-gray-mid max-w-prose leading-loose opacity-80">
              <li className="list-circle both">
                Feel “burnt out” and increasingly over-stressed?
              </li>
              <li className="list-circle both">
                Find it difficult to fall asleep or stay asleep at night?
              </li>
              <li className="list-circle both">Feel blue or anxious?</li>
              <li className="list-circle both">
                Wake up feeling tired and sluggish, and it lasts much of the
                day?
              </li>
              <li className="list-circle both">
                Feel “wired” and unable to fully relax?
              </li>
              <li className="list-circle both">
                Worry about your general health and well-being?
              </li>
            </ul>
          </div>
        </div>
      </div>
      <TopCurve
        fill="#39384A"
        className="-mt-4 md:-mt-8 xl:-mt-12 2xl:-mt-16 relative"
      />
      <div
        className="grid grid-cols-1 md:grid-cols-2"
        style={{ background: "#39384A" }}
      >
        <div className="text-white self-center 2xl:justify-self-end 2xl:mr-32">
          <p className="p-8 leading-loose max-w-md mx-auto md:text-18 md:leading-loose">
            Chill Out was specially formulated for those juggling the pressures
            of today’s fast-paced world. Busy lifestyles, minds, and routines
            have become the norm leaving many of us feeling anxious, stressed,
            and run down.
          </p>
        </div>

        <div className="relative">
          <div
            className="hidden md:block absolute left-0 top-0 bottom-0 w-36 z-10"
            style={{
              background:
                "linear-gradient(90deg, rgba(57,56,74,1) 0%, rgba(57,56,74,0) 100%)",
            }}
          ></div>
          <GatsbyImage
            image={busy}
            alt="Woman overwhelmed with work"
            className="h-80 md:h-full block"
            style={{ maxHeight: "550px" }}
          />
        </div>
      </div>
      <div style={{ background: "#F5C519" }}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:max-w-screen-xl xl:mx-auto">
          <div className="p-8 md:order-2 flex flex-col justify-center">
            <h2 className="text-40 text-white text-center">
              <span className="text-25" style={{ color: "#FFFFA6" }}>
                Five Powerful Plants.
                <br />
              </span>{" "}
              One Capsule.
            </h2>
            <p className="text-white mt-8 md:mt-0 md:p-8 pb-0 md:max-w-md md:mx-auto">
              Chill Out contains a combination of herbs used throughout the
              centuries in Western and Chinese herbal medicine.
            </p>
          </div>
          <div className="flex flex-col items-center justify-center overflow-hidden md:overflow-visible">
            <div className="relative mt-28 md:mt-32">
              <div className="absolute left-1/2 -mt-12 z-10">
                <div
                  id="plantParent"
                  className="rounded-full -left-1/2 relative"
                >
                  <Plant image={lemonBalm} name="Lemon Balm" />
                  <Plant image={passionflower} name="Passionflower" />
                  <Plant image={kavaKava} name="Kava Kava" />
                  <Plant image={magnolia} name="Magnolia" />
                  <Plant image={ziziphus} name="Ziziphus" />
                </div>
              </div>
              <GatsbyImage
                image={zen}
                alt="Man meditating in office chair in peace"
                className="xl:max-w-2xl h-72 w-96 md:w-full md:h-full block"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-dark p-6 relative z-20">
        <h2 className="text-35 md:text-40 text-white text-center">
          Results That Speak For Themselves.
        </h2>
        <div className="flex flex-col md:flex-row md:justify-center items-center">
          <CustomerQuote name="Lamisha" color="yellow-dark" isRight={true}>
            <p>
              <strong>Wow</strong>
              <br />I felt wired for a few days unable to come my nervous system
              down, I couldn’t focus. But after taking “Chill Out” I immediately
              felt calm.
            </p>
          </CustomerQuote>
          <CustomerQuote name="Marcus" color="yellow-dark" isRight={false}>
            <p>
              <strong>Chill Out Works</strong>
              <br />
              This stuff is amazing. Last few months it's been tough sleeping
              due to the stress and nature of my business. Taking this stuff has
              definitely improved my sleep patterns. Totally recommend this
              product.
            </p>
          </CustomerQuote>
          <CustomerQuote name="Valentin" color="yellow-dark" isRight={true}>
            <p>
              <strong>Works really well</strong>
              <br />I bought this supplement for my wife and she swears by it!
            </p>
          </CustomerQuote>
        </div>
      </div>
    </>
  );
};

export default ChillOut;

export const query = graphql`
  query {
    product: file(relativePath: { eq: "chillOut/chill-out-bottle.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    products: file(relativePath: { eq: "chillOut/chill-out-bottles.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    yoga: file(relativePath: { eq: "chillOut/yoga-at-sunset.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    busy: file(relativePath: { eq: "chillOut/too-busy.jpg" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    zen: file(relativePath: { eq: "chillOut/chill-out-chair.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    lemonBalm: file(relativePath: { eq: "chillOut/lemon-balm-icon.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    kavaKava: file(relativePath: { eq: "chillOut/kava-kava-icon.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    magnolia: file(relativePath: { eq: "chillOut/magnolia-icon.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    passionflower: file(
      relativePath: { eq: "chillOut/passionflower-icon.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
    ziziphus: file(relativePath: { eq: "chillOut/ziziphus-icon.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
  }
`;
